import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Building2, MapPin, Navigation, Loader2, Store, ArrowLeft, Tag, Share2, Check, Bell, BellRing } from "lucide-react";
import api from "../api";
import DealCard from "../components/dashboard/DealCard";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useSubscriptions } from "../context/SubscriptionsContext";
import { toast } from "../hooks/use-toast";
import { UndoAction } from "../components/UndoAction";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const slug = (v) => String(v).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const SpotSheet = () => {
  const { coord } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isFollowingSpot, toggleFollowSpot } = useSubscriptions();
  const [spot, setSpot] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setSpot(null); setError(false);
    api.get(`/spots/${coord}`).then((r) => setSpot(r.data)).catch(() => setError(true));
  }, [coord]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="spot-not-found">
        <p className="text-lg font-bold text-foreground">{t("spot.gone")}</p>
        <Link to="/lytemap" className="inline-flex items-center gap-2 mt-4 text-sm font-bold text-[#16a34a] hover:underline">
          <ArrowLeft size={15} /> {t("spot.backToMap")}
        </Link>
      </div>
    );
  }
  if (!spot) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#16a34a]" size={30} /></div>;
  }

  const title = spot.name || spot.address || t("spot.fallbackTitle");
  const shareUrl = `${BACKEND}/api/share/spot/${coord}`;
  // A WhatsApp-ready summary: the venue, then its best few deals, then the link.
  const top = spot.shops.flatMap((s) => s.deals.map((d) => ({ ...d, shop: s.business })))
    .sort((a, b) => (b.discount || 0) - (a.discount || 0)).slice(0, 4);
  const shareText = [
    t("spot.shareHead", { name: title, shops: spot.shopCount, deals: spot.dealCount }),
    ...top.map((d) => `• ${d.discount}% — ${d.title} (${d.shop})`),
    spot.dealCount > top.length ? t("spot.shareMore", { n: spot.dealCount - top.length }) : "",
    shareUrl,
  ].filter(Boolean).join("\n");

  const following = isFollowingSpot(coord);
  const onFollow = async () => {
    if (!user) {
      toast({ title: t("follows.signIn") });
      navigate("/signup");
      return;
    }
    const meta = { name: spot.name, address: spot.address, dealCount: spot.dealCount };
    const now = await toggleFollowSpot(coord, meta);
    toast({
      title: now ? t("follows.nowFollowing", { name: title }) : t("follows.unfollowed", { name: title }),
      description: now ? t("follows.placeNudge") : "",
      action: now ? undefined : <UndoAction testid="undo-unfollow-spot" onUndo={() => toggleFollowSpot(coord, meta)} />,
    });
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: shareText, url: shareUrl }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
      toast({ title: t("spot.shareCopied") });
    } catch {
      toast({ title: t("spot.shareCopied"), description: shareUrl });
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 lg:py-8 space-y-7" data-testid="spot-sheet">
      <Link to="/lytemap" className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-[#16a34a]" data-testid="spot-back">
        <ArrowLeft size={14} /> {t("spot.backToMap")}
      </Link>

      <header className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white p-6 lg:p-9">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#16a34a]/25 blur-2xl" />
        <div className="relative">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#7ee0a3]">
            <Building2 size={13} /> {t("spot.eyebrow")}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-2 leading-[1.05]" data-testid="spot-title">{title}</h1>
          <p className="text-white/70 text-sm mt-2 flex items-center gap-1.5">
            <MapPin size={14} className="text-[#7ee0a3]" /> {spot.address}{spot.country ? `, ${spot.country}` : ""}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-5">
            <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur text-white text-sm font-bold px-3.5 py-2 rounded-full" data-testid="spot-shop-count">
              <Store size={14} /> {t("map.shopsHere", { n: spot.shopCount })}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-[#16a34a] text-white text-sm font-bold px-3.5 py-2 rounded-full" data-testid="spot-deal-count">
              <Tag size={14} /> {t("spot.dealCount", { n: spot.dealCount })}
            </span>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${spot.lat},${spot.lng}`} target="_blank" rel="noopener noreferrer"
              data-testid="spot-directions"
              className="inline-flex items-center gap-1.5 bg-white text-[#0f2a1a] text-sm font-bold px-3.5 py-2 rounded-full hover:bg-[#e7f6ec] transition-colors">
              <Navigation size={14} /> {t("map.directions")}
            </a>
            <button onClick={share} data-testid="spot-share-btn"
              className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/25 text-white text-sm font-bold px-3.5 py-2 rounded-full hover:bg-white/20 transition-colors">
              {copied ? <Check size={14} /> : <Share2 size={14} />} {copied ? t("spot.shareCopied") : t("spot.share")}
            </button>
            <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer"
              data-testid="spot-share-whatsapp"
              className="inline-flex items-center gap-1.5 bg-[#25D366] text-white text-sm font-bold px-3.5 py-2 rounded-full hover:brightness-95 transition-all">
              {t("invite.whatsapp")}
            </a>
            <button onClick={onFollow} data-testid="spot-follow-btn"
              className={`inline-flex items-center gap-1.5 text-sm font-bold px-3.5 py-2 rounded-full transition-colors ${following ? "bg-[#16a34a] text-white hover:bg-[#15803d]" : "bg-white/10 backdrop-blur border border-white/25 text-white hover:bg-white/20"}`}>
              {following ? <BellRing size={14} /> : <Bell size={14} />} {following ? t("follows.following") : t("follows.followPlace")}
            </button>
          </div>
        </div>
      </header>

      {spot.shops.map((shop) => (
        <section key={shop.business} data-testid={`spot-shop-${slug(shop.business)}`}>
          <div className="flex items-end justify-between gap-3 mb-3">
            <h2 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#16a34a]" />
              {shop.business}
              <span className="text-xs font-semibold text-muted-foreground">{t("spot.dealCount", { n: shop.deals.length })}</span>
            </h2>
            <Link to={`/business/${encodeURIComponent(shop.business)}`} className="text-xs font-bold text-[#16a34a] hover:underline shrink-0"
              data-testid={`spot-shop-link-${slug(shop.business)}`}>
              {t("spot.visitShop")}
            </Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shop.deals.map((d) => <DealCard key={d.id} deal={d} />)}
          </div>
        </section>
      ))}
    </div>
  );
};

export default SpotSheet;
